import { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import {
  ArrowUp,
  Facebook,
  Instagram,
  Languages,
  Menu,
  Moon,
  Search,
  Sun,
  X,
  Youtube
} from "lucide-react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { usePreferences } from "../context/AppContext";
import { useContent } from "../hooks/useContent";
import { contentSearch } from "../utils/helpers";

const navItems = [
  ["home", "/"],
  ["about", "/about"],
  ["satsang", "/satsang"],
  ["events", "/events"],
  ["audio", "/audio"],
  ["videos", "/videos"],
  ["gallery", "/gallery"],
  ["contact", "/contact"],
  ["admin", "/admin/login"]
];

const officialFacebookUrl = "https://www.facebook.com/satnamsatsangahemdabad/";
const officialYoutubeUrl = "https://www.youtube.com/channel/UCQ_jrtXOsXaA8nyft9xCHMg";

export default function PublicLayout() {
  const { mode, setMode, language, setLanguage, t } = usePreferences();
  const { items: settingsItems } = useContent("websiteSettings");
  const settings = settingsItems[0] || {};
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showTop, setShowTop] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const results = useMemo(() => contentSearch(searchQuery), [searchQuery]);
  const brandLogo = settings.logoUrl || "/logo.png";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const whatsapp = settings.whatsapp || import.meta.env.VITE_WHATSAPP_NUMBER;

  if (settings.maintenanceMode) {
    return (
      <main className="maintenance-page">
        <img src={brandLogo} alt="Satnam Satsang Ahmedabad" />
        <span className="eyebrow">Satnam Satsang Ahmedabad</span>
        <Typography variant="h2">We’ll be back shortly</Typography>
        <Typography color="text.secondary">
          The website is receiving a little care. Thank you for your patience.
        </Typography>
        <Button component={Link} to="/admin/login">Administrator login</Button>
      </main>
    );
  }

  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <AppBar className="site-header" position="sticky" color="inherit" elevation={0}>
        <Toolbar className="container header-toolbar">
          <Link className="brand" to="/" aria-label="Satnam Satsang Ahmedabad home">
            <img src={brandLogo} alt="" />
            <span><strong>Satnam Satsang</strong><small>Ahmedabad</small></span>
          </Link>

          <nav className="desktop-nav" aria-label="Main navigation">
            {navItems.map(([label, url]) => (
              <NavLink key={url} to={url} end={url === "/"}>{t[label]}</NavLink>
            ))}
          </nav>

          <div className="header-actions">
            <Tooltip title={t.search}>
              <IconButton aria-label={t.search} onClick={() => setSearchOpen(true)}><Search size={20} /></IconButton>
            </Tooltip>
            <Tooltip title={language === "en" ? "हिंदी" : "English"}>
              <Button
                className="language-button"
                onClick={() => setLanguage(language === "en" ? "hi" : "en")}
                startIcon={<Languages size={18} />}
              >
                {language === "en" ? "हिंदी" : "EN"}
              </Button>
            </Tooltip>
            <Tooltip title={mode === "light" ? t.darkMode : t.lightMode}>
              <IconButton
                aria-label={mode === "light" ? t.darkMode : t.lightMode}
                onClick={() => setMode(mode === "light" ? "dark" : "light")}
              >
                {mode === "light" ? <Moon size={20} /> : <Sun size={20} />}
              </IconButton>
            </Tooltip>
            <IconButton className="menu-button" aria-label="Open menu" onClick={() => setDrawerOpen(true)}>
              <Menu />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <div className="mobile-drawer">
          <div className="drawer-heading">
            <div className="brand"><img src={brandLogo} alt="" /><span><strong>Satnam Satsang</strong><small>Ahmedabad</small></span></div>
            <IconButton aria-label="Close menu" onClick={() => setDrawerOpen(false)}><X /></IconButton>
          </div>
          <List>
            {navItems.map(([label, url]) => (
              <ListItemButton key={url} component={NavLink} to={url}>
                <ListItemText primary={t[label]} />
              </ListItemButton>
            ))}
          </List>
        </div>
      </Drawer>

      <Dialog open={searchOpen} onClose={() => setSearchOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle className="dialog-heading">
          Search Satnam Satsang
          <IconButton aria-label="Close search" onClick={() => setSearchOpen(false)}><X /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="Try “audio”, “events” or “contact”"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search size={20} /></InputAdornment> } }}
          />
          <List className="search-results">
            {results.map((result) => (
              <ListItemButton key={result.url} onClick={() => {
                navigate(result.url);
                setSearchOpen(false);
                setSearchQuery("");
              }}>
                <ListItemText primary={result.title} secondary={result.description} />
              </ListItemButton>
            ))}
            {searchQuery && !results.length && <Typography color="text.secondary">No matching pages found.</Typography>}
          </List>
        </DialogContent>
      </Dialog>

      <main id="main-content"><Outlet /></main>

      <footer className="site-footer">
        <div className="container footer-grid">
          <div className="footer-about">
            <Link className="brand inverse" to="/"><img src={brandLogo} alt="" /><span><strong>Satnam Satsang</strong><small>Ahmedabad</small></span></Link>
            <Typography>{settings.shortDescription || "Sacred sound, shared stillness and loving service."}</Typography>
            <div className="social-links">
              <IconButton component="a" href={settings.facebook || officialFacebookUrl} target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook /></IconButton>
              {settings.instagram && <IconButton component="a" href={settings.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram /></IconButton>}
              <IconButton component="a" href={settings.youtube || officialYoutubeUrl} target="_blank" rel="noreferrer" aria-label="YouTube"><Youtube /></IconButton>
            </div>
          </div>
          <div>
            <h3>Explore</h3>
            <Link to="/about">About us</Link>
            <Link to="/satsang">Satsang programmes</Link>
            <Link to="/audio">Audio library</Link>
            <Link to="/gallery">Gallery</Link>
          </div>
          <div>
            <h3>Information</h3>
            <Link to="/contact">Contact us</Link>
            <Link to="/privacy">Privacy policy</Link>
            <Link to="/terms">Terms & conditions</Link>
            <Link to="/disclaimer">Disclaimer</Link>
          </div>
          <div>
            <h3>Visit us</h3>
            <Typography>{settings.address || "Mahendergarh, Haryana · Mukundgarh, Rajasthan"}</Typography>
            {settings.phone && <a href={`tel:${settings.phone}`}>{settings.phone}</a>}
            {settings.email && <a href={`mailto:${settings.email}`}>{settings.email}</a>}
            <Button component={Link} to="/contact" variant="outlined" color="inherit">Get directions</Button>
          </div>
        </div>
        <div className="container footer-bottom">
          <span>© {new Date().getFullYear()} Satnam Satsang Ahmedabad. All rights reserved.</span>
          <span>Made with श्रद्धा in Ahmedabad</span>
        </div>
      </footer>

      <Box
        className={`floating-actions ${showTop ? "visible" : ""}`}
        aria-label="Quick actions"
      >
        <Tooltip title={whatsapp ? "Message us on WhatsApp" : "Contact Satnam Satsang"}>
          <IconButton
            component={whatsapp ? "a" : Link}
            href={whatsapp ? `https://wa.me/${String(whatsapp).replace(/\D/g, "")}` : undefined}
            to={whatsapp ? undefined : "/contact"}
            target={whatsapp ? "_blank" : undefined}
            rel={whatsapp ? "noreferrer" : undefined}
            className="whatsapp-button"
            aria-label="Contact on WhatsApp"
          >
            <span>WA</span>
          </IconButton>
        </Tooltip>
        <Tooltip title="Back to top">
          <IconButton onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top">
            <ArrowUp />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );
}
